import React from 'react';
import { IonButton, IonPopover, IonCheckbox, IonLabel } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FilterIcon } from '../../theme/Icons';
import { ICareMapSection } from '../../caremap/ICareMap';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

const useStyles = createUseStyles({
    filterButton: {
        '--padding-start': '8px',
        '--padding-end': '8px',
        fontSize: '14px !important',
        fontWeight: '500 !important',
        height: 'auto',
        minHeight: '36px',
    },
    filterPopover: {
        '&::part(content)': {
            minWidth: 200,
            minHeight: 'auto',
            maxHeight: '80vh',
            overflowY: 'auto'
        }
    },
    filterSection: {
        padding: '0 0 12px 0'
    },
    sectionHeader: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--ion-text-color)',
        padding: '16px 16px 8px 16px',
        margin: '0'
    },
    filterCount: {
        fontSize: '12px',
        backgroundColor: 'var(--ion-color-primary)',
        color: 'white',
        borderRadius: '10px',
        padding: '2px 6px',
        marginLeft: '4px',
        minWidth: '18px',
        width: '18px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: '2px',
        top: '-6px',
        zIndex: 1000
    },
    filterButtonWrapper: {
        position: 'relative',
        display: 'inline-block'
    },
    filterOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        cursor: 'pointer',
        minHeight: '40px',
        '&:hover': {
            backgroundColor: 'var(--ion-color-step-50, #f2f2f2)'
        }
    },
});

export interface FilterState {
    noteType: string[];
    careCategory: string[];
    tags: string[];
}

interface INotesFilterControlProps {
    filters: FilterState;
    onFiltersChange: (filters: FilterState) => void;
    notes: any[];
    triggerId?: string;
    isMobile?: boolean;
    hideCareCategory?: boolean;
    iconOnly?: boolean;
}

export const NotesFilterControl: React.FC<INotesFilterControlProps> = ({
    filters,
    onFiltersChange,
    notes,
    triggerId = 'filter-trigger',
    hideCareCategory = false,
    iconOnly = false
}) => {
    const classes = useStyles();
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const noteTypeOptions = [
        { key: 'hasActionItems', label: 'Action Items' },
        { key: 'hasRecording', label: 'Recording' },
        { key: 'hasOtherAttachments', label: 'Other Attachments' }
    ];

    // Get care categories that actually exist in the notes (based on actual note assignments)
    const getAvailableCareCategoryOptions = () => {
        const availableSections = new Set<ICareMapSection>();
        notes.forEach(note => {
            const section = note.otherAttributes?.noteTarget?.section;
            if (section) {
                availableSections.add(section);
            }
        });

        const sectionLabelMap: Record<ICareMapSection, string> = {
            'patient': 'Care Recipient',
            'household': 'Inner Circle',
            'medical': 'Primary Care',
            'specialists': 'Specialists',
            'school': 'School',
            'community': 'Community',
            'financial': 'Financial & Legal',
            'social': 'Living'
        };

        return Array.from(availableSections)
            .filter(section => sectionLabelMap[section])
            .map(section => ({
                key: section,
                label: sectionLabelMap[section]
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    };

    const careCategoryOptions = getAvailableCareCategoryOptions();

    // Get tags that actually exist in the notes
    const getAvailableTagOptions = () => {
        const availableTags = new Set<string>();
        notes.forEach(note => {
            if (note.tags && Array.isArray(note.tags)) {
                note.tags.forEach((tag: string) => availableTags.add(tag));
            }
        });

        return Array.from(availableTags)
            .sort((a, b) => a.localeCompare(b))
            .map(tag => ({
                key: tag,
                label: tag
            }));
    };

    const tagOptions = getAvailableTagOptions();

    const handleFilterChange = (category: keyof FilterState, value: string, checked: boolean) => {
        const currentFilters = filters[category];
        const newFilters = checked
            ? [...currentFilters, value]
            : currentFilters.filter(f => f !== value);

        // Update filters immediately for responsive UI
        onFiltersChange({
            ...filters,
            [category]: newFilters
        });

        // Track asynchronously to avoid blocking UI
        const otherFilters = Object.keys(filters)
            .filter(key => key !== category)
            .reduce((count, key) => count + filters[key as keyof FilterState].length, 0);
        const totalActiveFilters = newFilters.length + otherFilters;
        try {
            if (checked) {
                mixpanel.track('Notes Filter Applied', () => ({
                    'Filter Category': category,
                    'Filter Value': value,
                    'Is Enabled': true,
                    'Total Active Filters': totalActiveFilters
                }));
            } else {
                mixpanel.track('Notes Filter Cleared', () => ({
                    'Cleared Filter Category': category,
                    'Remaining Active Filters': totalActiveFilters
                }));
            }
        } catch (error) {
            // Silently continue if tracking fails
        }
    };

    const getTotalActiveFilters = () => {
        return Object.values(filters).reduce((total, filterArray) => total + filterArray.length, 0);
    };

    const activeFilterCount = getTotalActiveFilters();

    const renderFilterSection = (
        title: string,
        category: keyof FilterState,
        options: { key: string; label: string }[]
    ) => (
        <div className={classes.filterSection}>
            <div className={classes.sectionHeader}>{title}</div>
            <div>
                {options.map(option => (
                    <div
                        key={option.key}
                        className={classes.filterOption}
                        onClick={() => handleFilterChange(category, option.key, !filters[category].includes(option.key))}
                        data-id={`filter-${category}-${option.key}`}
                    >
                        <IonCheckbox
                            checked={filters[category].includes(option.key)}
                            onIonChange={(e) => {
                                e.stopPropagation();
                                handleFilterChange(category, option.key, e.detail.checked);
                            }}
                        />
                        <IonLabel style={{ marginLeft: '6px', fontSize: '14px' }}>{option.label}</IonLabel>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <>
            <div className={classes.filterButtonWrapper}>
                <IonButton
                    fill="clear"
                    color="medium"
                    id={triggerId}
                    onClick={() => setPopoverOpen(true)}
                    className={classes.filterButton}
                    data-id="filter-button"
                >
                    <FilterIcon size="medium" color="medium" />
                    {!iconOnly && <span style={{ marginLeft: '4px' }}>Filter</span>}
                </IonButton>
                {activeFilterCount > 0 && (
                    <span className={classes.filterCount} data-id="filter-count">{activeFilterCount}</span>
                )}
            </div>

            <IonPopover
                isOpen={popoverOpen}
                trigger={triggerId}
                alignment="end"
                onDidDismiss={() => setPopoverOpen(false)}
                className={classes.filterPopover}
            >
                <div style={{ padding: '8px 0' }}>
                    {renderFilterSection('Contains', 'noteType', noteTypeOptions)}

                    {!hideCareCategory && careCategoryOptions.length > 0 && (
                        <>
                            <div style={{ height: '1px', backgroundColor: 'var(--ion-color-step-150, #e0e0e0)', margin: '6px 0' }} />
                            {renderFilterSection('Care Category', 'careCategory', careCategoryOptions)}
                        </>
                    )}

                    {tagOptions.length > 0 && (
                        <>
                            <div style={{ height: '1px', backgroundColor: 'var(--ion-color-step-150, #e0e0e0)', margin: '6px 0' }} />
                            {renderFilterSection('Tags', 'tags', tagOptions)}
                        </>
                    )}
                </div>
            </IonPopover>
        </>
    );
};
